import { a as __rest } from '../../tslib.es6.esm.js';
import { jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { simulateButtonKeyboardBehavior } from '../../utils/buttonKeyboardBehavior.esm.js';
import { tv } from 'tailwind-variants';
import { focusStyles } from '../../styles/focusStyles.esm.js';
import { ButtonContent } from './components/Content/ButtonContent.esm.js';

// Define the button styles using the tv function
const buttonStyles = tv({
    // Base styles applied to all buttons
    slots: {
        base: `
      iris:inline-flex
      iris:cursor-pointer
      iris:items-center
      iris:justify-center
      iris:gap-(--comp-space-gap-button-icon)
      iris:rounded-(--comp-size-radius-button)
      iris:border
      iris:border-(length:--comp-size-border-button)
      iris:py-(--comp-space-padding-button-vertical)
      iris:font-(family-name:--comp-type-font-button)
      iris:font-(--comp-type-weight-button)!
      iris:[text-transform:var(--comp-type-transform-button)]
      iris:no-underline
      iris:duration-(--comp-motion-duration-button)
      iris:ease-(--comp-motion-easing-button)
    `,
    },
    variants: {
        // Size variants for the button
        size: {
            sm: {
                base: `
          iris:h-(--comp-size-height-button-sm)
          iris:px-(--comp-space-padding-button-sm-horizontal)
          iris:text-(length:--comp-type-size-button-sm)
        `,
            },
            md: {
                base: `
          iris:h-(--comp-size-height-button-md)
          iris:px-(--comp-space-padding-button-md-horizontal)
          iris:text-(length:--comp-type-size-button-md)
        `,
            },
            lg: {
                base: `
          iris:h-(--comp-size-height-button-lg)
          iris:px-(--comp-space-padding-button-lg-horizontal)
          iris:text-(length:--comp-type-size-button-lg)
        `,
            },
        },
        // Responsive size variants
        smSize: {
            sm: {
                base: `
          iris:sm:h-(--comp-size-height-button-sm)
          iris:sm:px-(--comp-space-padding-button-sm-horizontal)
          iris:sm:text-(length:--comp-type-size-button-sm)
        `,
            },
            md: {
                base: `
          iris:sm:h-(--comp-size-height-button-md)
          iris:sm:px-(--comp-space-padding-button-md-horizontal)
          iris:sm:text-(length:--comp-type-size-button-md)
        `,
            },
            lg: {
                base: `
          iris:sm:h-(--comp-size-height-button-lg)
          iris:sm:px-(--comp-space-padding-button-lg-horizontal)
          iris:sm:text-(length:--comp-type-size-button-lg)
        `,
            },
        },
        mdSize: {
            sm: {
                base: `
          iris:md:h-(--comp-size-height-button-sm)
          iris:md:px-(--comp-space-padding-button-sm-horizontal)
          iris:md:text-(length:--comp-type-size-button-sm)
        `,
            },
            md: {
                base: `
          iris:md:h-(--comp-size-height-button-md)
          iris:md:px-(--comp-space-padding-button-md-horizontal)
          iris:md:text-(length:--comp-type-size-button-md)
        `,
            },
            lg: {
                base: `
          iris:md:h-(--comp-size-height-button-lg)
          iris:md:px-(--comp-space-padding-button-lg-horizontal)
          iris:md:text-(length:--comp-type-size-button-lg)
        `,
            },
        },
        lgSize: {
            sm: {
                base: `
          iris:lg:h-(--comp-size-height-button-sm)
          iris:lg:px-(--comp-space-padding-button-sm-horizontal)
          iris:lg:text-(length:--comp-type-size-button-sm)
        `,
            },
            md: {
                base: `
          iris:lg:h-(--comp-size-height-button-md)
          iris:lg:px-(--comp-space-padding-button-md-horizontal)
          iris:lg:text-(length:--comp-type-size-button-md)
        `,
            },
            lg: {
                base: `
          iris:lg:h-(--comp-size-height-button-lg)
          iris:lg:px-(--comp-space-padding-button-lg-horizontal)
          iris:lg:text-(length:--comp-type-size-button-lg)
        `,
            },
        },
        // Style variants for the button
        variant: {
            primary: {},
            secondary: {},
            tertiary: {},
            unstyled: {},
        },
        inverse: {
            true: {
                base: focusStyles.inverse,
            },
            false: {
                base: focusStyles.default,
            },
        },
        disabled: {
            true: { base: "iris:cursor-not-allowed" },
            false: {},
        },
        isLoading: {
            true: { base: "iris:cursor-not-allowed" },
            false: "",
        },
    },
    // Compound variants for the button
    compoundVariants: [
        {
            variant: "unstyled",
            disabled: false,
            inverse: false,
            className: {
                base: `
          iris:h-auto
          iris:border-none
          iris:bg-transparent
          iris:p-0
        `,
            },
        },
        {
            variant: "primary",
            disabled: false,
            inverse: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-primary-rest)
          iris:bg-(--comp-color-background-button-primary-rest)
          iris:text-(--comp-color-content-button-primary-rest)
          iris:hover:border-(--comp-color-border-button-primary-hover)
          iris:hover:bg-(--comp-color-background-button-primary-hover)
          iris:hover:text-(--comp-color-content-button-primary-hover)
          iris:active:border-(--comp-color-border-button-primary-pressed)
          iris:active:bg-(--comp-color-background-button-primary-pressed)
          iris:active:text-(--comp-color-content-button-primary-pressed)
        `,
            },
        },
        {
            variant: "primary",
            disabled: false,
            inverse: true,
            className: {
                base: `
          iris:border-(--comp-color-border-button-primary-inverse-rest)
          iris:bg-(--comp-color-background-button-primary-inverse-rest)
          iris:text-(--comp-color-content-button-primary-inverse-rest)
          iris:hover:border-(--comp-color-border-button-primary-inverse-hover)
          iris:hover:bg-(--comp-color-background-button-primary-inverse-hover)
          iris:hover:text-(--comp-color-content-button-primary-inverse-hover)
          iris:active:border-(--comp-color-border-button-primary-inverse-pressed)
          iris:active:bg-(--comp-color-background-button-primary-inverse-pressed)
          iris:active:text-(--comp-color-content-button-primary-inverse-pressed)
        `,
            },
        },
        {
            variant: "secondary",
            disabled: false,
            inverse: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-secondary-rest)
          iris:bg-(--comp-color-background-button-secondary-rest)
          iris:text-(--comp-color-content-button-secondary-rest)
          iris:hover:border-(--comp-color-border-button-secondary-hover)
          iris:hover:bg-(--comp-color-background-button-secondary-hover)
          iris:hover:text-(--comp-color-content-button-secondary-hover)
          iris:active:border-(--comp-color-border-button-secondary-pressed)
          iris:active:bg-(--comp-color-background-button-secondary-pressed)
          iris:active:text-(--comp-color-content-button-secondary-pressed)
        `,
            },
        },
        {
            variant: "secondary",
            disabled: false,
            inverse: true,
            className: {
                base: `
          iris:border-(--comp-color-border-button-secondary-inverse-rest)
          iris:bg-(--comp-color-background-button-secondary-inverse-rest)
          iris:text-(--comp-color-content-button-secondary-inverse-rest)
          iris:hover:border-(--comp-color-border-button-secondary-inverse-hover)
          iris:hover:bg-(--comp-color-background-button-secondary-inverse-hover)
          iris:hover:text-(--comp-color-content-button-secondary-inverse-hover)
          iris:active:border-(--comp-color-border-button-secondary-inverse-pressed)
          iris:active:bg-(--comp-color-background-button-secondary-inverse-pressed)
          iris:active:text-(--comp-color-content-button-secondary-inverse-pressed)
        `,
            },
        },
        {
            variant: "tertiary",
            disabled: false,
            inverse: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-tertiary-rest)
          iris:bg-(--comp-color-background-button-tertiary-rest)
          iris:text-(--comp-color-content-button-tertiary-rest)
          iris:hover:border-(--comp-color-border-button-tertiary-hover)
          iris:hover:bg-(--comp-color-background-button-tertiary-hover)
          iris:hover:text-(--comp-color-content-button-tertiary-hover)
          iris:active:border-(--comp-color-border-button-tertiary-pressed)
          iris:active:bg-(--comp-color-background-button-tertiary-pressed)
          iris:active:text-(--comp-color-content-button-tertiary-pressed)
        `,
            },
        },
        {
            variant: "tertiary",
            disabled: false,
            inverse: true,
            className: {
                base: `
          iris:border-(--comp-color-border-button-tertiary-inverse-rest)
          iris:bg-(--comp-color-background-button-tertiary-inverse-rest)
          iris:text-(--comp-color-content-button-tertiary-inverse-rest)
          iris:hover:border-(--comp-color-border-button-tertiary-inverse-hover)
          iris:hover:bg-(--comp-color-background-button-tertiary-inverse-hover)
          iris:hover:text-(--comp-color-content-button-tertiary-inverse-hover)
          iris:active:border-(--comp-color-border-button-tertiary-inverse-pressed)
          iris:active:bg-(--comp-color-background-button-tertiary-inverse-pressed)
          iris:active:text-(--comp-color-content-button-tertiary-inverse-pressed)
        `,
            },
        },
        {
            variant: "primary",
            disabled: true,
            inverse: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-primary-disabled)
          iris:bg-(--comp-color-background-button-primary-disabled)
          iris:text-(--comp-color-content-button-primary-disabled)
          iris:opacity-(--sem-opacity-interactive-disabled)
        `,
            },
        },
        {
            variant: "primary",
            disabled: true,
            inverse: true,
            className: {
                base: `
          iris:border-(--comp-color-border-button-primary-inverse-disabled)
          iris:bg-(--comp-color-background-button-primary-inverse-disabled)
          iris:text-(--comp-color-content-button-primary-inverse-disabled)
          iris:opacity-(--sem-opacity-interactive-disabled)
        `,
            },
        },
        {
            variant: "secondary",
            disabled: true,
            inverse: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-secondary-disabled)
          iris:bg-(--comp-color-background-button-secondary-disabled)
          iris:text-(--comp-color-content-button-secondary-disabled)
          iris:opacity-(--sem-opacity-interactive-disabled)
        `,
            },
        },
        {
            variant: "secondary",
            disabled: true,
            inverse: true,
            className: {
                base: `
          iris:border-(--comp-color-border-button-secondary-inverse-disabled)
          iris:bg-(--comp-color-background-button-secondary-inverse-disabled)
          iris:text-(--comp-color-content-button-secondary-inverse-disabled)
          iris:opacity-(--sem-opacity-interactive-disabled)
        `,
            },
        },
        {
            variant: "tertiary",
            disabled: true,
            inverse: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-tertiary-disabled)
          iris:bg-(--comp-color-background-button-tertiary-disabled)
          iris:text-(--comp-color-content-button-tertiary-disabled)
          iris:opacity-(--sem-opacity-interactive-disabled)
        `,
            },
        },
        {
            variant: "tertiary",
            disabled: true,
            inverse: true,
            className: {
                base: `
          iris:border-(--comp-color-border-button-tertiary-inverse-disabled)
          iris:bg-(--comp-color-background-button-tertiary-inverse-disabled)
          iris:text-(--comp-color-content-button-tertiary-inverse-disabled)
          iris:opacity-(--sem-opacity-interactive-disabled)
        `,
            },
        },
        // Loading states - using disabled tokens
        {
            variant: "primary",
            isLoading: true,
            inverse: false,
            disabled: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-primary-disabled)
          iris:bg-(--comp-color-background-button-primary-disabled)
          iris:hover:border-(--comp-color-border-button-primary-disabled)
          iris:hover:bg-(--comp-color-background-button-primary-disabled)
          iris:active:border-(--comp-color-border-button-primary-disabled)
          iris:active:bg-(--comp-color-background-button-primary-disabled)
        `,
            },
        },
        {
            variant: "primary",
            isLoading: true,
            inverse: true,
            disabled: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-primary-inverse-disabled)
          iris:bg-(--comp-color-background-button-primary-inverse-disabled)
          iris:hover:border-(--comp-color-border-button-primary-inverse-disabled)
          iris:hover:bg-(--comp-color-background-button-primary-inverse-disabled)
          iris:active:border-(--comp-color-border-button-primary-inverse-disabled)
          iris:active:bg-(--comp-color-background-button-primary-inverse-disabled)
        `,
            },
        },
        {
            variant: "secondary",
            isLoading: true,
            inverse: false,
            disabled: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-secondary-disabled)
          iris:bg-(--comp-color-background-button-secondary-disabled)
          iris:hover:border-(--comp-color-border-button-secondary-disabled)
          iris:hover:bg-(--comp-color-background-button-secondary-disabled)
          iris:active:border-(--comp-color-border-button-secondary-disabled)
          iris:active:bg-(--comp-color-background-button-secondary-disabled)
        `,
            },
        },
        {
            variant: "secondary",
            isLoading: true,
            inverse: true,
            disabled: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-secondary-inverse-disabled)
          iris:bg-(--comp-color-background-button-secondary-inverse-disabled)
          iris:hover:border-(--comp-color-border-button-secondary-inverse-disabled)
          iris:hover:bg-(--comp-color-background-button-secondary-inverse-disabled)
          iris:active:border-(--comp-color-border-button-secondary-inverse-disabled)
          iris:active:bg-(--comp-color-background-button-secondary-inverse-disabled)
        `,
            },
        },
        {
            variant: "tertiary",
            isLoading: true,
            inverse: false,
            disabled: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-tertiary-disabled)
          iris:bg-(--comp-color-background-button-tertiary-disabled)
          iris:hover:border-(--comp-color-border-button-tertiary-disabled)
          iris:hover:bg-(--comp-color-background-button-tertiary-disabled)
          iris:active:border-(--comp-color-border-button-tertiary-disabled)
          iris:active:bg-(--comp-color-background-button-tertiary-disabled)
        `,
            },
        },
        {
            variant: "tertiary",
            isLoading: true,
            inverse: true,
            disabled: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-tertiary-inverse-disabled)
          iris:bg-(--comp-color-background-button-tertiary-inverse-disabled)
          iris:hover:border-(--comp-color-border-button-tertiary-inverse-disabled)
          iris:hover:bg-(--comp-color-background-button-tertiary-inverse-disabled)
          iris:active:border-(--comp-color-border-button-tertiary-inverse-disabled)
          iris:active:bg-(--comp-color-background-button-tertiary-inverse-disabled)
        `,
            },
        },
    ],
    defaultVariants: {
        size: "md",
        variant: "primary",
        inverse: false,
        disabled: false,
    },
});

/**
 * Button component communicates an action will occur, like advancing to the next page, or saving information. Button labels clearly describe the action they trigger.
 */
const Button = forwardRef((_a, ref) => {
    var { variant = "primary", size = "md", className = "", children, onClick, inverse = false, disabled = false, isLoading = false, loadingMessage = "Loading", icon, iconPosition = "left", href } = _a, props = __rest(_a, ["variant", "size", "className", "children", "onClick", "inverse", "disabled", "isLoading", "loadingMessage", "icon", "iconPosition", "href"]);
    const hasHref = Boolean(href);
    const hasIcon = Boolean(icon);
    const isInteractionDisabled = disabled || isLoading;
    const styleOptions = {
        size: typeof size === "string" ? size : (size === null || size === void 0 ? void 0 : size.xs) || undefined,
        smSize: typeof size === "object" ? size === null || size === void 0 ? void 0 : size.sm : undefined,
        mdSize: typeof size === "object" ? size === null || size === void 0 ? void 0 : size.md : undefined,
        lgSize: typeof size === "object" ? size === null || size === void 0 ? void 0 : size.lg : undefined,
        variant,
        inverse,
        disabled,
        class: className,
        isLoading,
    };
    const classes = buttonStyles(styleOptions);
    const handleClick = (e) => {
        // Prevent all interactions when loading or disabled
        if (isInteractionDisabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        // Call onClick if provided
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    const Component = href ? "a" : "button";
    const componentProps = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, props), { ref, className: classes.base({ class: className }), onClick: handleClick }), (isInteractionDisabled && {
        "aria-disabled": true,
        "aria-live": "polite",
    })), (isLoading && { "aria-label": `${loadingMessage}` })), (!hasHref && { disabled: isInteractionDisabled })), (hasHref && Object.assign({}, (isInteractionDisabled
        ? { href: undefined, role: "link" }
        : { href, onKeyDown: simulateButtonKeyboardBehavior(onClick) }))));
    return (jsx(Component, Object.assign({}, componentProps, { children: jsx(ButtonContent, { isLoading: isLoading, loadingMessage: loadingMessage, size: size, icon: hasIcon ? icon : undefined, iconPosition: iconPosition, hasIcon: hasIcon, inverse: inverse, variant: variant, children: children }) })));
});
Button.displayName = "Button";

export { Button };
